import React, { ReactNode } from "react";
import styled from "styled-components";
import { typographyCSS } from "@ifgengineering/design-tokens/legacy";
import { cardStrokeColor } from "@styles/cardStroke";
import {
  InvestableGlyph,
  V3Badge,
  V3Card,
  V3Icon,
  V3Text,
} from "@components/v3/primitives";

const ICON_TILE = 28;
const ICON_GLYPH = 20;
const CHECK_SIZE = 12;
const PILL_ICON = 12;

const Shell = styled(V3Card)`
  border-color: ${({ theme }) => cardStrokeColor(theme, "strong")};
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

/** A tinted band separating who the deal is from what it pays. */
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md}px;
  padding: ${({ theme }) => theme.space.md}px
    ${({ theme }) => theme.space.card.inset}px;
  background: ${({ theme }) =>
    theme.colors.ui.interactive.primaryDeepTintSubtle};
  border-bottom: ${({ theme }) => theme.stroke.md}px solid
    ${({ theme }) => theme.colors.ui.border.divider};
`;

const IconTile = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${ICON_TILE}px;
  height: ${ICON_TILE}px;
  border-radius: ${({ theme }) => theme.radius.xs}px;
  background: ${({ theme }) =>
    theme.mode === "dark"
      ? theme.colors.ui.interactive.primaryDeepTintSubtle
      : theme.colors.ui.interactive.primaryDeepTintMedium};
  color: ${({ theme }) =>
    theme.mode === "dark"
      ? theme.colors.ui.icon.primary
      : theme.colors.ui.icon.brand};
`;

const Name = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space["3xs"]}px;
`;

const Tag = styled(V3Badge)`
  border-radius: ${({ theme }) => theme.radius.xs}px;
`;

const IsaTag = styled(Tag)`
  background: ${({ theme }) =>
    theme.mode === "dark"
      ? theme.colors.domainSpecific.entityTypes.chipIsa
      : theme.colors.domainSpecific.entityTypes.ifisaBg};
  color: ${({ theme }) =>
    theme.mode === "dark"
      ? theme.colors.ui.text.primary
      : theme.colors.domainSpecific.entityTypes.ifisa};
`;

const TermTag = styled(Tag)`
  background: ${({ theme }) =>
    theme.mode === "dark"
      ? theme.colors.ui.background.info
      : theme.colors.ui.interactive.primaryDeepTintMedium};
  ${({ theme }) =>
    theme.mode === "dark" &&
    `
      color: ${theme.colors.ui.text.primary};

      svg {
        color: ${theme.colors.ui.icon.info};
      }
    `}
`;

const Tags = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm}px;
`;

const Body = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md}px;
  padding: ${({ theme }) => theme.space.card.inset}px;
`;

const StatsRow = styled.dl`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  margin: 0;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space["3xs"]}px;
  min-width: 0;
  padding-left: ${({ theme }) => theme.space.md}px;
  border-left: ${({ theme }) => theme.stroke.md}px solid
    ${({ theme }) => theme.colors.ui.border.divider};

  &:first-child {
    padding-left: 0;
    border-left: none;
  }
`;

const StatLabel = styled.dt`
  ${typographyCSS("caption.sm")}
  margin: 0;
  color: ${({ theme }) => theme.colors.ui.text.tertiary};
`;

const StatValue = styled.dd`
  ${typographyCSS("label.sm-semibold")}
  margin: 0;
  color: ${({ theme }) => theme.colors.ui.text.primary};
`;

/* Pinned to the bottom so a row of cards lines its footers up. */
const TaxBenefit = styled.p`
  ${typographyCSS("caption.sm-medium")}
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm}px;
  margin: auto 0 0;
  color: ${({ theme }) => theme.colors.ui.text.success};
`;

export interface DealCardStat {
  label: string;
  value: string;
}

export interface DealCardShellProps {
  name: string;
  /** Short descriptor under the header, e.g. "Fixed Income" or "3–12 month notice". */
  tagline?: string;
  /** Terms available, e.g. "6M" or "3M · 6M · 12M". */
  termChip?: string;
  /** Open and close dates, e.g. "Open · Closes 20 Sep" or "Opens 2 Sep · Closes 4 Oct". */
  statusText?: string;
  isaEligible: boolean;
  stats: DealCardStat[];
  /** Fund tenor pills, capacity gauges — anything below the stats. */
  children?: ReactNode;
  onSelect: () => void;
  testID: string;
  icon?: string;
  investableType?: string;
}

export const DealCardShell = ({
  name,
  tagline,
  termChip,
  statusText,
  isaEligible,
  stats,
  children,
  onSelect,
  testID,
  icon,
  investableType,
}: DealCardShellProps): JSX.Element => (
  <Shell padding="none" interactive onClick={onSelect} testID={testID}>
    <Header>
      <IconTile aria-hidden="true">
        <InvestableGlyph
          icon={icon}
          investableType={investableType}
          size={ICON_GLYPH}
        />
      </IconTile>
      <Name>
        <V3Text variant="label.sm-semibold" as="h3">
          {name}
        </V3Text>
        {statusText ? (
          <V3Text
            variant="caption.sm-medium"
            style={{ fontSize: 11, lineHeight: "14px" }}
            color="secondary"
          >
            {statusText}
          </V3Text>
        ) : null}
      </Name>
      <Tags>
        {isaEligible ? (
          <IsaTag tone="successSubtle" size="sm">
            ISA
          </IsaTag>
        ) : null}
        {termChip ? (
          <TermTag tone="accent" size="sm">
            <V3Icon name="calendar" size={PILL_ICON} color="currentColor" />
            {termChip}
          </TermTag>
        ) : null}
      </Tags>
    </Header>

    <Body>
      {tagline ? (
        <V3Text variant="heading.2xs" as="p">
          {tagline}
        </V3Text>
      ) : null}

      {stats.length > 0 ? (
        <StatsRow>
          {stats.map((stat) => (
            <Stat key={stat.label}>
              <StatLabel>{stat.label}</StatLabel>
              <StatValue>{stat.value}</StatValue>
            </Stat>
          ))}
        </StatsRow>
      ) : null}

      {children}

      {isaEligible ? (
        <TaxBenefit>
          <V3Icon name="check" size={CHECK_SIZE} color="currentColor" />
          UK tax benefits
        </TaxBenefit>
      ) : null}
    </Body>
  </Shell>
);
